//Imports
import React, { useEffect, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Modal,
    IconButton,
    Select
} from "@gpstrackit/gtc-react-controls";

//Views
import CreateEditContact from '../../../contactTab/modals/createEditContact'
import CreateEditLocation from '../../../locationTab/modals/createEditLocation'
import CreateEditCustomer from '../../../customerTab/modals/createEditCustomer'

//Actions
import {
    openModalCreateEditContact,
    openModalCreateEditLocation,
    openModalCreateEditCustomer
} from "Redux/actions";
import {
    workOrdersSelectorCustomerSearchCustomers,
    workOrdersSelectorCustomerSearchLocations,
    workOrdersSelectorCustomerSearchContacts,
    resetWorkOrderCustomerSelector
} from "./redux/slice";

const INIT_DATA = {
    customer: null,
    customerContact: null,
    customerLocation: null
};

const CustomerSelector = ({ currentId }) => {

    const dispatch = useDispatch();
    const { messages } = useIntl();

    //States
    const [formData, setFormData] = useState(INIT_DATA);

    //Form refs
    const driverSelectorWorkOrderFormRef = Form.useForm();

    //Redux
    const {
        loading,
        open,
        customers,
        loadingCustomers,
        locations,
        loadingLocations,
        contacts,
        loadingContacts,
        selectedCustomer,
        locationId,
        contactId,
        customerId
    } = useSelector((state) => state.workOrdersCustomerSelectorRedux);
    // Obtener el driver que viene del scheduler
    const { preloadedData } = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux);

    const openModalContact = useSelector((state) => state.workOrdersCreateEditContactRedux.open);
    const openModalLocation = useSelector((state) => state.workOrdersCreateEditLocationRedux.open);
    const openModalCustomer = useSelector((state) => state.workOrdersCreateEditCustomerRedux.open);

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(resetWorkOrderCustomerSelector());
    };

    const applyChanges = () => {
        dispatch(resetWorkOrderCustomerSelector({
            open: false,
            selectedCustomer: {
                ...formData
            }
        }));
    }

    useEffect(() => {
        if (open) {
            if (selectedCustomer) {
                setFormData({
                    ...formData,
                    customer: {
                        id: selectedCustomer?.id,
                        name: selectedCustomer?.name,
                        tags: selectedCustomer?.tags?.length ? selectedCustomer?.tags : [],
                        tagIds: selectedCustomer?.tagIds || []
                    },
                    customerContact: selectedCustomer?.customerContact,
                    customerLocation: selectedCustomer?.customerLocation
                })
                setTimeout(() => {
                    driverSelectorWorkOrderFormRef.setValues({
                        customer: { id: selectedCustomer?.id, name: selectedCustomer?.name },
                        customerContact: selectedCustomer?.customerContact,
                        customerLocation: selectedCustomer?.customerLocation
                    })
                }, 500)
                dispatch(workOrdersSelectorCustomerSearchLocations({ customerId: selectedCustomer?.id }))
                dispatch(workOrdersSelectorCustomerSearchContacts({ customerId: selectedCustomer?.id }))
            }
            
            // Si viene del scheduler y tiene un driver con tagIds, filtrar clientes
            if (preloadedData?.driver?.tagIds && preloadedData.driver.tagIds.length > 0) {
            // When you need to perform the search:
            const conditions = [
                { 
                    fields: ["tagIds"],
                    terms: preloadedData.driver.tagIds.map(id => parseInt(id)),
                    exact: true
                }
            ];
                dispatch(workOrdersSelectorCustomerSearchCustomers({ conditions }));
            } else {
                // Comportamiento normal cuando no viene del scheduler
                dispatch(workOrdersSelectorCustomerSearchCustomers());
            }
        }
    }, [open, preloadedData])

    useEffect(() => {
        if (locationId) {
            dispatch(workOrdersSelectorCustomerSearchLocations({ customerId: formData?.customer?.id }))
        }
    }, [locationId])

    useEffect(() => {
        if (contactId) {
            dispatch(workOrdersSelectorCustomerSearchContacts({ customerId: formData?.customer?.id }))
        }
    }, [contactId])

    useEffect(() => {
        if (customerId) {
            dispatch(workOrdersSelectorCustomerSearchLocations({ customerId: customerId }))
            dispatch(workOrdersSelectorCustomerSearchContacts({ customerId: customerId }))
            dispatch(workOrdersSelectorCustomerSearchCustomers())
        }
    }, [customerId])

    useEffect(() => {
        if (customers?.length && customerId) {
            let newCustomer = customers.find((item) => item?.id === customerId)
            if (newCustomer) {
                let payload = {
                    customer: {
                        id: newCustomer?.id,
                        name: newCustomer?.name,
                        tags: newCustomer?.tags?.length ? newCustomer?.tags : [],
                        tagIds: newCustomer?.tagIds || []
                    },
                    customerLocation: newCustomer?.customerLocation,
                    customerContact: newCustomer?.customerContact
                }
                setFormData({ ...formData, ...payload })
                setTimeout(() => {
                    driverSelectorWorkOrderFormRef.setValues({ ...payload })
                }, 500)
            }

            dispatch(resetWorkOrderCustomerSelector({ customerId: null }))
        }
    }, [customers])

    useEffect(() => {
        if (locations.length && locationId) {
            let newLocation = locations.find((item) => item?.id === locationId)
            setFormData({ ...formData, customerLocation: newLocation })
            setTimeout(() => {
                driverSelectorWorkOrderFormRef.setValues({ customerLocation: newLocation })
            }, 500)
            dispatch(resetWorkOrderCustomerSelector({ locationId: null }))
        }
    }, [locations])

    useEffect(() => {
        if (contacts.length && contactId) {
            let newContact = contacts.find((item) => item?.id === contactId)
            setFormData({ ...formData, customerContact: newContact })
            setTimeout(() => {
                driverSelectorWorkOrderFormRef.setValues({ customerContact: newContact })
            }, 500)
            dispatch(resetWorkOrderCustomerSelector({ contactId: null }))
        }
    }, [contacts])

    return (
        <Modal
            title={messages['workOrder_customerSelector_title']}
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading || loadingContacts || loadingCustomers || loadingLocations}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            driverSelectorWorkOrderFormRef?.submit()
                        }}
                        variant="primary"
                    >
                        {messages["select"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: '24px 24px 0 24px' }}>


                <Form
                    ref={driverSelectorWorkOrderFormRef.ref}
                    onValidSubmit={() => applyChanges()}
                >
                    {(!openModalCustomer && !openModalLocation && !openModalContact) ? (
                        <>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: '90%' }}>
                                    <Form.Item
                                        name="customer"
                                        label={messages['workOrder_customerSelector_customer']}
                                        rules={[
                                            { required: true, message: 'required_field' }
                                        ]}
                                    >
                                        <Select
                                            onChange={(value) => {
                                                if (value?.id) {
                                                    dispatch(workOrdersSelectorCustomerSearchLocations({ customerId: value?.id }))
                                                    dispatch(workOrdersSelectorCustomerSearchContacts({ customerId: value?.id }))
                                                } else {
                                                    dispatch(resetWorkOrderCustomerSelector({ contacts: [], locations: [] }))
                                                }
                                                setFormData({
                                                    ...formData,
                                                    customer: value,
                                                    customerContact: null,
                                                    customerContact: null
                                                })
                                                setTimeout(() => {
                                                    driverSelectorWorkOrderFormRef.setValues({
                                                        customerContact: null,
                                                        customerLocation: null
                                                    })
                                                }, 500)
                                            }}
                                            id="customer"
                                            options={customers || []}
                                            textField="name"
                                            valueField="id"
                                            placeholder={messages['select']}
                                            maxLength={40}
                                            isClearable
                                            isSearchable
                                            value={formData?.customer}
                                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                            isDisabled={!!currentId}
                                            isLoading={loadingCustomers}
                                        />
                                    </Form.Item>
                                </div>
                                {
                                    !currentId &&
                                    <div style={{ width: '10%' }}>
                                        <IconButton
                                            icon="add"
                                            size="medium"
                                            tooltip={messages['workOrder_customerSelector_create_new_customer']}
                                            style={{ marginTop: 25, marginLeft: 5 }}
                                            onClick={() => dispatch(openModalCreateEditCustomer())}
                                        />
                                    </div>
                                }
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: '90%' }}>
                                    <Form.Item
                                        name="customerContact"
                                        label={messages['workOrder_customerSelector_contact']}
                                        rules={[
                                            { required: true, message: 'required_field' }
                                        ]}
                                    >
                                        <Select
                                            onChange={(value) => {
                                                setFormData({ ...formData, customerContact: value })
                                            }}
                                            id="customerContact"
                                            value={formData?.customerContact}
                                            options={contacts || []}
                                            textField="name"
                                            valueField="id"
                                            placeholder={messages['select']}
                                            maxLength={40}
                                            createOptions
                                            isClearable
                                            isSearchable
                                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                            isDisabled={!formData?.customer}
                                            isLoading={loadingContacts}
                                        />
                                    </Form.Item>
                                </div>
                                <div style={{ width: '10%' }}>
                                    <IconButton
                                        icon="add"
                                        size="medium"
                                        tooltip={messages['workOrder_customerSelector_create_new_contact']}
                                        style={{ marginTop: 25, marginLeft: 5 }}
                                        onClick={() => dispatch(openModalCreateEditContact({ customerId: formData?.customer?.id }))}
                                        isDisabled={!formData?.customer}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: '90%' }}>
                                    <Form.Item
                                        name="customerLocation"
                                        label={messages['workOrder_customerSelector_location']}
                                        rules={[
                                            { required: true, message: 'required_field' }
                                        ]}
                                    >
                                        <Select
                                            onChange={(value) => {
                                                setFormData({ ...formData, customerLocation: value })
                                            }}
                                            value={formData?.customerLocation}
                                            id="customerLocation"
                                            options={locations || []}
                                            textField="name"
                                            valueField="id"
                                            placeholder={messages['select']}
                                            maxLength={40}
                                            isClearable
                                            isSearchable
                                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                            isDisabled={!formData?.customer}
                                            isLoading={loadingLocations}
                                        />
                                    </Form.Item>
                                </div>
                                <div style={{ width: '10%' }}>
                                    <IconButton
                                        icon="add"
                                        size="medium"
                                        tooltip={messages['workOrder_customerSelector_create_new_location']}
                                        style={{ marginTop: 25, marginLeft: 5 }}
                                        onClick={() => dispatch(openModalCreateEditLocation({ customerId: formData?.customer?.id }))}
                                        isDisabled={!formData?.customer}
                                    />
                                </div>
                            </div>
                        </>
                    ) : <></>}
                </Form>


            </div>
            {openModalContact ? <CreateEditContact /> : <></>}
            {openModalLocation ? <CreateEditLocation /> : <></>}
            {openModalCustomer ? <CreateEditCustomer /> : <></>}
        </Modal>
    );
};

export default CustomerSelector;